import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Button, Space, Modal, Form, Input, InputNumber,
  Tag, Popconfirm, message, Typography, Alert, Tooltip,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined,
  DatabaseOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { adbDemoAPI, DemoItem } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';

const { Text } = Typography;

interface HealthInfo {
  enabled: boolean;
  connected: boolean;
  db_name?: string;
  service?: string;
  count?: number;
  detail?: string;
}

const AdbDemoPage: React.FC = () => {
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('adb_demo', 'WRITE');
  const canDelete = hasPermission('adb_demo', 'DELETE');

  const [items, setItems] = useState<DemoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<HealthInfo | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DemoItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const loadHealth = useCallback(async () => {
    try {
      const res = await adbDemoAPI.health();
      setHealth(res.data);
    } catch (e: any) {
      setHealth({ enabled: false, connected: false, detail: e?.message });
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adbDemoAPI.list();
      setItems(res.data.items || []);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'ADB 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
    loadItems();
  }, [loadHealth, loadItems]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ qty: 0 });
    setModalOpen(true);
  };

  const openEdit = (rec: DemoItem) => {
    setEditing(rec);
    form.setFieldsValue({ name: rec.name, qty: rec.qty, note: rec.note });
    setModalOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await adbDemoAPI.update(editing.id, values);
        message.success('수정되었습니다.');
      } else {
        await adbDemoAPI.create(values);
        message.success('추가되었습니다.');
      }
      setModalOpen(false);
      await Promise.all([loadItems(), loadHealth()]);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rec: DemoItem) => {
    try {
      await adbDemoAPI.remove(rec.id);
      message.success('삭제되었습니다.');
      await Promise.all([loadItems(), loadHealth()]);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  const columns: ColumnsType<DemoItem> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '이름', dataIndex: 'name', render: (v) => <Text strong>{v}</Text> },
    { title: '수량', dataIndex: 'qty', width: 100, render: (v) => <Tag color="blue">{v}</Tag> },
    { title: '비고', dataIndex: 'note', ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: '생성일시', dataIndex: 'created_at', width: 190,
      render: (v) => (v ? new Date(v).toLocaleString('ko-KR') : '—'),
    },
    {
      title: '작업', key: 'action', width: 140, fixed: 'right',
      render: (_, rec) => (
        <Space>
          <Tooltip title={canWrite ? '수정' : '권한 없음'}>
            <Button size="small" icon={<EditOutlined />} disabled={!canWrite} onClick={() => openEdit(rec)} />
          </Tooltip>
          <Popconfirm title="삭제하시겠습니까?" okText="삭제" cancelText="취소"
            disabled={!canDelete} onConfirm={() => remove(rec)}>
            <Tooltip title={canDelete ? '삭제' : '권한 없음'}>
              <Button size="small" danger icon={<DeleteOutlined />} disabled={!canDelete} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const connected = health?.connected;

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={<Space><DatabaseOutlined style={{ color: '#4f46e5' }} />OCI Autonomous Database 데모 (CRUD)</Space>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => { loadItems(); loadHealth(); }}>새로고침</Button>
            <Button type="primary" icon={<PlusOutlined />} disabled={!canWrite || !connected} onClick={openCreate}>
              항목 추가
            </Button>
          </Space>
        }
      >
        <Alert
          style={{ marginBottom: 16 }}
          type={connected ? 'success' : 'warning'}
          showIcon
          icon={connected ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          message={
            connected
              ? `ADB 연결됨 · DB: ${health?.db_name || '-'} · 서비스: ${health?.service || '-'} · 행 수: ${health?.count ?? '-'}`
              : `ADB 미연결${health?.detail ? ` — ${health.detail}` : ''}`
          }
          description={
            <Text type="secondary">
              DEFAULT 프로파일(춘천)의 Autonomous Database <code>SAPPTDEMO</code> 의 <code>demo_items</code> 테이블에 대한
              생성·조회·수정·삭제 예제입니다. (PostgreSQL 메인 DB와 별개의 2차 DB)
            </Text>
          }
        />
        <Table
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          scroll={{ x: 800 }}
          pagination={{ pageSize: 10, showTotal: (t) => `총 ${t}건` }}
        />
      </Card>

      <Modal
        title={editing ? '항목 수정' : '항목 추가'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        confirmLoading={saving}
        okText={editing ? '수정' : '추가'}
        cancelText="취소"
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ qty: 0 }}>
          <Form.Item name="name" label="이름" rules={[{ required: true, message: '이름을 입력하세요.' }, { max: 200 }]}>
            <Input placeholder="예: 샘플 약제 D" maxLength={200} />
          </Form.Item>
          <Form.Item name="qty" label="수량" rules={[{ required: true, message: '수량을 입력하세요.' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="note" label="비고" rules={[{ max: 1000 }]}>
            <Input.TextArea rows={3} maxLength={1000} placeholder="설명(선택)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdbDemoPage;
